import React from 'react';
import Icon from '../../../components/AppIcon';
import { TRADEMARK_DEFINITIONS } from '../../../constants/contentConfig';

const StreamlinedMethodology = () => {
  const phases = [
    {
      phase: '1',
      title: 'Strategic Alignment',
      duration: '10 Days',
      track: 'Sequential',
      icon: 'Target',
      color: 'bg-blue-600',
      description: 'Board-ready business case and executive alignment',
      outcomes: [
        'CXO stakeholder workshops',
        'Market viability assessment',
        'Financial projections & ROI model',
        'Board presentation deck'
      ]
    },
    {
      phase: '2',
      title: 'Foundation Setup',
      duration: '6 Weeks',
      track: 'Parallel with Phase 3',
      icon: 'Building2',
      color: 'bg-green-600',
      description: 'Legal entity, banking, compliance, and infrastructure',
      outcomes: [
        'Complete legal entity formation',
        'Banking & treasury setup',
        'Regulatory compliance (100% guaranteed)',
        'Zero-trust IT infrastructure'
      ]
    },
    {
      phase: '3',
      title: 'Talent Acquisition',
      duration: '8 Weeks',
      track: 'Parallel with Phase 2',
      icon: 'Users',
      color: 'bg-purple-600',
      description: 'Leadership team and initial workforce deployment',
      outcomes: [
        'First 5 leaders hired',
        'Talent Cloud 100™ delivered',
        'Compensation benchmarking',
        'Onboarding framework'
      ]
    },
    {
      phase: '4',
      title: 'Operational Launch',
      duration: '2 Weeks',
      track: 'Sequential',
      icon: 'Rocket',
      color: 'bg-orange-600',
      description: 'Go-live with full operational capabilities',
      outcomes: [
        'Process & governance launch',
        'Quality management systems',
        'Performance metrics dashboard',
        'Executive cockpit reporting'
      ]
    }
  ];

  const definitions = [
    TRADEMARK_DEFINITIONS.LaunchBlueprint,
    TRADEMARK_DEFINITIONS.TalentCloud100
  ];

  const guarantees = [
    {
      title: '90-Day Timeline',
      description: 'Operational GCC or fee waiver',
      icon: 'Clock',
      color: 'text-blue-600'
    },
    {
      title: 'Compliance Assurance',
      description: 'Zero violations guaranteed',
      icon: 'Shield',
      color: 'text-green-600'
    },
    {
      title: 'Talent Delivery',
      description: '5 leaders + 100 CVs in 8 weeks',
      icon: 'UserCheck',
      color: 'text-purple-600'
    },
    {
      title: 'Fixed Pricing',
      description: 'No budget overruns, ever',
      icon: 'Lock',
      color: 'text-orange-600'
    }
  ];

  return (
    <section className="py-20 bg-muted/30">
      <div className="container-executive">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Workflow" size={16} />
            <span>Proven 90-Day Methodology</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-headline font-bold text-text-primary mb-6">
            From Decision to
            <span className="block text-accent">Operational Excellence</span>
          </h2>

          <p className="text-xl text-text-secondary">
            Four clear phases. Guaranteed outcomes. No complexity, no delays.
          </p>
          <p className="mt-4 text-sm text-text-secondary">
            Phases 2 and 3 run on parallel tracks — that&apos;s how 90 days is achievable for what would otherwise take 4–5 months sequentially.
          </p>
        </div>

        {/* Timeline Visualization */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="grid md:grid-cols-4 gap-8 relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-16 left-0 right-0 h-0.5 bg-border" style={{ left: '12.5%', right: '12.5%' }}></div>
            
            {phases.map((phase, index) => (
              <div key={index} className="relative">
                <div className="card-elevated rounded-xl p-6 relative z-10">
                  {/* Phase Number Badge */}
                  <div className={`absolute -top-4 left-6 w-12 h-12 ${phase.color} rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
                    {phase.phase}
                  </div>

                  {/* Icon */}
                  <div className={`w-14 h-14 ${phase.color} rounded-xl flex items-center justify-center mb-4 mt-4`}>
                    <Icon name={phase.icon} size={28} color="white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-headline font-bold text-text-primary mb-2">
                    {phase.title}
                  </h3>
                  <div className="text-sm font-semibold text-accent mb-1">{phase.duration}</div>
                  {phase.track && (
                    <div className="text-xs text-text-secondary mb-3 italic">{phase.track}</div>
                  )}
                  <p className="text-sm text-text-secondary mb-4">{phase.description}</p>

                  {/* Outcomes */}
                  <ul className="space-y-2">
                    {phase.outcomes.map((outcome, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-xs text-text-secondary">
                        <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                        <span>{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Total Timeline */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-3 bg-primary/10 rounded-full px-8 py-4">
            <Icon name="Calendar" size={24} className="text-primary" />
            <div className="text-left">
              <div className="text-sm text-text-secondary">Total Timeline</div>
              <div className="text-2xl font-bold text-text-primary">90 Days to Operational</div>
            </div>
          </div>
        </div>

        {/* Guarantees Grid */}
        <div className="card-elevated rounded-2xl p-8 lg:p-12 bg-gradient-to-br from-primary/5 to-accent/5">
          <h3 className="text-3xl font-headline font-bold text-text-primary text-center mb-8">
            Every Phase, Guaranteed
          </h3>
          
          <div className="grid md:grid-cols-4 gap-6">
            {guarantees.map((guarantee, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-background rounded-xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                  <Icon name={guarantee.icon} size={32} className={guarantee.color} />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">{guarantee.title}</h4>
                <p className="text-sm text-text-secondary">{guarantee.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 pt-8 border-t border-border">
            <p className="text-text-secondary mb-2">
              If we miss any commitment, you don't pay for that phase.
            </p>
            <p className="text-lg font-semibold text-text-primary">
              That's our guarantee. That's the Bootminds difference.
            </p>
          </div>
        </div>

        {/* Definitions */}
        <div className="mt-12 max-w-4xl mx-auto">
          <h3 className="text-lg font-semibold text-text-primary mb-4 text-center">What we mean by</h3>
          <dl className="grid md:grid-cols-2 gap-6">
            {definitions.map((def) => (
              <div key={def.name} className="rounded-xl border border-border bg-card p-5">
                <dt className="font-semibold text-text-primary mb-1">{def.name}</dt>
                <dd className="text-sm text-text-secondary">{def.short}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
};

export default StreamlinedMethodology;
